import React from "react";
import { Card, Container, Badge } from "react-bootstrap";
import { useTheme } from "../ThemeContext";

const TermsOfService = () => {
  const { isDarkMode } = useTheme();
  const themeClass = isDarkMode ? "bg-dark text-light" : "bg-light text-dark";
  const borderClass = isDarkMode ? "border-secondary" : "border-light";

  return (
    <Container className="d-flex justify-content-center align-items-center py-5">
      <Card className={`shadow-lg p-4 w-100 ${themeClass} ${borderClass}`} style={{ maxWidth: "900px" }}>
        <Card.Body>
          <div className="text-center mb-4">
            <Badge bg="primary" className="mb-2">BETA TESTING AGREEMENT</Badge>
            <h2 className="h3 mb-2">Pipsqueak Network Scanner</h2>
            <p className="text-muted">Beta Testing Terms & Conditions</p>
          </div>

          <div
            style={{
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "20px",
              border: `1px solid ${isDarkMode ? "#495057" : "#dee2e6"}`,
              borderRadius: "8px",
              background: isDarkMode ? "#1a1d20" : "#f8f9fa",
              lineHeight: "1.6"
            }}
            className={isDarkMode ? "text-light" : "text-dark"}
          >
            <div className="border-bottom pb-3 mb-3">
              <p className="mb-1"><strong>Effective Date:</strong> September 1, 2025</p>
            </div>

            <div className="alert alert-warning mb-4" role="alert">
              <strong className="d-block mb-1">IMPORTANT NOTICE</strong>
              Please read this Agreement carefully before participating in the program.
            </div>

            <p className="fw-bold mb-4">
              By participating in the Closed Beta Program for the Pipsqueak Network Scanner
              ("Software") provided by Southwestern R&amp;D LLC dba BUSINESS TECHNOLOGY
              SOLUTIONS GROUP "BTSG" ("Company," "we," "us," or "our"), you ("Tester")
              acknowledge that you have read, understood, and agree to be legally bound by
              the terms of this Agreement.
            </p>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">1. Beta Participation and Relationship</h5>
              <p className="mb-0">
                Participation in the Beta Program is strictly voluntary. This Agreement does not
                establish any partnership, agency, employment, or joint venture relationship
                between the Tester and the Company.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">2. Eligibility Requirements</h5>
              <p className="mb-0">
                You represent and warrant that you are at least 18 years of age and possess
                the legal capacity and authority to enter into this binding Agreement.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">3. Legal Agreement</h5>
              <p className="mb-0">
                This Agreement constitutes a binding legal contract between you and the Company.
                Your use of the Software and participation in the Beta Program are expressly
                subject to compliance with these terms.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">4. License Grant and Restrictions</h5>
              <p>
                Subject to the terms herein, we grant you a limited, revocable, non-exclusive,
                non-transferable, non-sublicensable license to use the Software solely for
                Beta testing and feedback purposes during the Beta Period.
              </p>
              <p className="fw-semibold">You agree not to:</p>
              <ul className="list-unstyled ms-3">
                <li>• Copy, reverse engineer, decompile, disassemble, or modify the Software</li>
                <li>• Share, distribute, publish, or disclose the Software or any related materials</li>
                <li>• Utilize the Software in production or commercial environments</li>
                <li>• Bypass any security mechanisms or access controls</li>
              </ul>
              <p className="mb-0">
                All intellectual property rights, title, and interest in the Software remain
                the exclusive property of the Company.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">5. Confidentiality Obligations</h5>
              <p className="mb-0">
                You agree to maintain the confidentiality of all information related to the Software,
                testing activities, and this Agreement, and shall not disclose such information
                to any third party without prior written consent.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">6. Feedback and Contributions</h5>
              <ul className="list-unstyled ms-3">
                <li>• All feedback, suggestions, and improvements provided become the exclusive property of the Company</li>
                <li>• We reserve the right to utilize feedback for any business purpose without compensation or attribution</li>
              </ul>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">7. Data Collection and Privacy</h5>
              <p className="fw-semibold">We collect and process the following data categories:</p>
              <ul className="list-unstyled ms-3">
                <li><strong>Account Data:</strong> Name, email, payment information (if applicable); retained indefinitely</li>
                <li><strong>Scan Data:</strong> Information gathered during network scans; deleted upon Beta conclusion</li>
                <li><strong>Technical Data:</strong> IP addresses, device information, browser type, timestamps, usage patterns</li>
              </ul>
              <p className="mb-0">
                All data is encrypted both at rest and in transit. Data deletion requests may be
                submitted to <a href="mailto:helpdesk@btsg.io" className="text-decoration-none">helpdesk@btsg.io</a>.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">8. Warranty Disclaimer</h5>
              <p className="fst-italic mb-0">
                THE SOFTWARE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES
                OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
                IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                OR NON-INFRINGEMENT.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">9. Limitation of Liability</h5>
              <p className="fst-italic mb-0">
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL
                THE COMPANY BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL,
                SPECIAL, OR PUNITIVE DAMAGES ARISING FROM OR RELATED TO THIS AGREEMENT.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">10. Support and Service Availability</h5>
              <p className="mb-0">
                The Company is under no obligation to provide technical support or maintain
                specific service availability levels. Access to the Beta Program may be
                interrupted or discontinued at our sole discretion.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">11. Termination Rights</h5>
              <ul className="list-unstyled ms-3">
                <li>• We reserve the right to suspend or terminate Beta access at any time</li>
                <li>• You may terminate participation by ceasing all use of the Software and providing written notice</li>
              </ul>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">12. Agreement Duration</h5>
              <ul className="list-unstyled ms-3">
                <li>• Continues until the official conclusion of the Beta Program</li>
                <li>• Earlier termination by either party pursuant to Section 11</li>
                <li>• Superseded by a commercial license agreement, if applicable</li>
              </ul>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">13. Governing Law</h5>
              <p className="mb-0">
                This Agreement shall be governed by and construed in accordance with the
                laws of the State of Texas, without regard to its conflict of law principles.
              </p>
            </section>

            <section className="mb-4">
              <h5 className="text-primary border-bottom pb-2">14. Tester Responsibilities</h5>
              <p className="mb-0">
                You assume full responsibility for any damages, claims, or liabilities
                arising from misuse, violation of this Agreement, or unauthorized disclosure
                of confidential information.
              </p>
            </section>

            <div className="border-top pt-3 mt-4">
              <p className="mb-1"><strong>Contact Information</strong></p>
              <p className="mb-0">
                For questions regarding this Agreement or the Beta Program, please contact:
                <a href="mailto:helpdesk@btsg.io" className="text-decoration-none ms-1">helpdesk@btsg.io</a>
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default TermsOfService;